import { Field, FieldLabel } from "@nt/ui/field";
import { CircleAlert } from "lucide-react";
import { useState } from "react";

import {
  SIGN_IN_PATH,
  postSignInDestination,
  signInWithPassword,
} from "../auth/password-login";
import { AuthLayout } from "./auth-layout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@nt/ui/button";
import { Input } from "@/components/ui/input";

/**
 * One way in: email and password, posted same-origin to this console, which
 * forwards to the auth Worker — so nothing in the login path touches another
 * host.
 *
 * The admin-key box that used to sit below it is gone (goal 231). It was the
 * recovery path while the browser login was unproven in production; goal 230
 * proved it there, and the owner's decision was to take the box off the screen
 * rather than tuck it behind a link. A key still AUTHENTICATES — an agent
 * presents one as a bearer and `worker/auth.ts` accepts it exactly as before,
 * which `worker/index.test.ts` pins. What left is the affordance, not the
 * credential: a key is no longer something a human pastes into this screen.
 *
 * There is no second button. A PKCE route used to sit here behind an unset
 * `VITE_*` flag, dark since the day it was written; it redirected to the
 * Vercel-fronted app for its sign-in and consent pages, which is the thing this
 * console exists to get off the login path. Goal 229 deleted it.
 *
 * Signing in grants nothing by itself. It only produces a credential; the
 * Worker re-derives the admin verdict from D1 on every single request, so a
 * non-admin who signs in successfully still gets a 403 on the very next call.
 */
export function SignIn({
  reason,
  destination,
  subtitle = "Sign in with your staff account.",
}: {
  reason?: string;
  /**
   * Where a successful sign-in lands. Absent, it is the link the operator
   * followed (`postSignInDestination`). The operator connector's `/oauth/sign-in`
   * passes the way back into the pending authorization instead.
   */
  destination?: string;
  subtitle?: string;
}) {
  // The gate's reason ("Sign in to continue.") is a notice, not an error: it
  // shows until the first attempt, and a failed attempt shows as an error.
  const [notice, setNotice] = useState<string | null>(reason ?? null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);

  async function submitPassword(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setPending(true);

    // The POST, and the credential hand-over that has to follow it, both live
    // in signInWithPassword — including dropping any bearer this tab still
    // holds, which is what stops a stale admin key from deciding the very next
    // request. See app/auth/password-login.ts.
    const outcome = await signInWithPassword({ email, password });

    if (!outcome.ok) {
      setError(outcome.message);
      setPending(false);
      return;
    }

    // Nothing to store: the session is an HttpOnly cookie the browser now
    // holds. A full reload rather than a router push so the gate re-runs
    // /api/admin/me with the new cookie and the password leaves React state —
    // but to the link the operator actually followed, which the gate kept in
    // the URL while it showed this screen.
    window.location.assign(
      destination ?? postSignInDestination(window.location),
    );
  }

  return (
    <AuthLayout
      title="Welcome back"
      subtitle={subtitle}
      footer="Staff accounts only. Your admin role is checked on every request."
    >
      <div className="space-y-6">
        {error ? (
          <Alert variant="destructive">
            <CircleAlert aria-hidden="true" />
            <AlertTitle>{error}</AlertTitle>
          </Alert>
        ) : null}
        {notice ? (
          <Alert role="status">
            <AlertDescription>{notice}</AlertDescription>
          </Alert>
        ) : null}

        <form
          action={SIGN_IN_PATH}
          method="post"
          onSubmit={(event) => void submitPassword(event)}
          className="space-y-4"
        >
          <Field>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@nonresident.tax"
              className="h-11"
            />
          </Field>
          <Field>
            <div className="flex items-center justify-between">
              <FieldLabel htmlFor="password">Password</FieldLabel>
              <a
                href="/forgot-password"
                className="text-sm font-medium text-primary underline-offset-4 hover:underline"
              >
                Forgot password?
              </a>
            </div>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="h-11"
            />
          </Field>
          <Button type="submit" size="lg" className="w-full" disabled={pending}>
            {pending ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
